import * as React from 'react';
import styles from './AdminPanel.module.scss';

interface IUserRow {
  id: number;
  email: string;
  displayName: string;
  role: 'admin' | 'client';
  clientId: number | null;
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const UsersManager: React.FC = () => {
  const [users, setUsers] = React.useState<IUserRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [draft, setDraft] = React.useState({
    email: '', displayName: '', password: '', role: 'client' as 'admin' | 'client',
  });

  const refresh = React.useCallback(() => {
    setLoading(true);
    api<IUserRow[]>('GET', '/users')
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleAdd = async (): Promise<void> => {
    if (!draft.email || !draft.password) return;
    await api('POST', '/users', draft);
    setDraft({ email: '', displayName: '', password: '', role: 'client' });
    refresh();
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!confirm('Supprimer cet utilisateur ?')) return;
    await api('DELETE', `/users/${id}`);
    refresh();
  };

  const handleRoleChange = async (id: number, role: 'admin' | 'client'): Promise<void> => {
    await api('PUT', `/users/${id}`, { role });
    refresh();
  };

  if (loading) return <div className={styles.loading}>Chargement…</div>;

  return (
    <div className={styles.crud}>
      <h3>Utilisateurs ({users.length})</h3>
      <div className={styles.addRow}>
        <input
          className={styles.input}
          placeholder="Email"
          value={draft.email}
          onChange={e => setDraft({ ...draft, email: e.target.value })}
        />
        <input
          className={styles.input}
          placeholder="Nom affiché"
          value={draft.displayName}
          onChange={e => setDraft({ ...draft, displayName: e.target.value })}
        />
        <input
          className={styles.input}
          type="password"
          placeholder="Mot de passe"
          value={draft.password}
          onChange={e => setDraft({ ...draft, password: e.target.value })}
        />
        <select
          className={styles.input}
          value={draft.role}
          onChange={e => setDraft({ ...draft, role: e.target.value as 'admin' | 'client' })}
        >
          <option value="client">client</option>
          <option value="admin">admin</option>
        </select>
        <button className={styles.btnPrimary} onClick={handleAdd}>Ajouter</button>
      </div>
      <table className={styles.table}>
        <thead>
          <tr><th>Email</th><th>Nom</th><th>Rôle</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.displayName}</td>
              <td>
                <select
                  className={styles.input}
                  value={u.role}
                  onChange={e => handleRoleChange(u.id, e.target.value as 'admin' | 'client')}
                >
                  <option value="client">client</option>
                  <option value="admin">admin</option>
                </select>
              </td>
              <td>
                <button className={styles.btnSmallDanger} onClick={() => handleDelete(u.id)}>
                  Supprimer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
